import { EPaletteModes } from '@/types/theme-packs.types';
import {
  parseColor,
  resolveColor,
  formatTimestamp,
  eventImage,
  getHeaderImage,
  getBtvFallbackForBg,
  description as getDescription,
} from './utils/utils';
import type { TemplateProps } from './index';
import chroma from 'chroma-js';
import ContentEditable from './content-editable';
import { useImageUpload } from './utils/use-image-upload';
import styles from './template-styles.module.css';
import getRelativeContrastColor from '@/utils/colors/generate-relative-contrasting-color';

export const Hollywood = ({
  event,
  theme,
  mode,
  calendarLinks,
  customizations,
  href,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  startIntro,
  setStartIntro,
  setMainImage,
  setHeaderImage,
}: TemplateProps) => {
  const resolvedMode = mode ?? event.settings.design.color_theme ?? theme.theme_mode ?? EPaletteModes.Light;
  const baseBg = resolvedMode === EPaletteModes.Dark ? '#000000' : '#FFFFFF';
  const themeColors = theme.colors[resolvedMode] ?? theme.colors;
  const bg =
    customizations?.backgroundColor ||
    chroma
      .mix(
        baseBg,
        customizations?.bodyTextColor || parseColor(themeColors.bodyTextColor, { opacity: 0.5 }),
        0.05,
        'rgb'
      )
      .css();
  const container = resolveColor(themeColors.containerColor, customizations?.containerColor);
  const accent = resolveColor(themeColors.accentColor, customizations?.accentColor);
  const heading = getRelativeContrastColor(
    resolveColor(themeColors.headingTextColor, customizations?.headingTextColor),
    container
  );
  const body = getRelativeContrastColor(resolveColor(themeColors.bodyTextColor, customizations?.bodyTextColor), accent);
  const buttonText = getRelativeContrastColor(
    resolveColor(themeColors.buttonTextColor ?? themeColors.bodyTextColor, customizations?.buttonTextColor),
    container
  );
  const [loadingMain, uploadMain] = useImageUpload(setMainImage);
  const [loadingHeaderImg, uploadHeaderImg] = useImageUpload(setHeaderImage);
  const sessionDate = event.sessions[0]?.timestamp ? formatTimestamp(event.sessions[0].timestamp) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || getBtvFallbackForBg(accent);
  const bodyImg = customizations?.mainImage || eventImage(event);
  const headingFont = customizations?.headingFont
    ? `${customizations.headingFont}, sans-serif`
    : 'Arial Black, sans-serif';
  const bodyFont = customizations?.bodyFont ? `${customizations.bodyFont}, sans-serif` : 'Trebuchet MS, sans-serif';
  const _salutation = salutation ?? (customizations?.salutation || "YOU'RE INVITED TO:");
  const eventTitle = title ?? (customizations?.title || event.name);
  const eventDescription = description ?? customizations?.description ?? getDescription(event, 'en', 200, 3, 14);
  const _startIntro = startIntro ?? (customizations?.startIntro || 'The show starts on');
  const _cta = cta ?? (customizations?.cta || 'REGISTER');

  return (
    <table
      data-component="Hollywood"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--body': body,
          '--heading': heading,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '40px 40px 24px 40px' }}>
            <table
              width={520}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '520px' }}
            >
              <tbody>
                <tr>
                  <td style={{ background: 'var(--container)' }}>
                    <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td
                            align="center"
                            valign="middle"
                            height={48}
                            style={{ background: 'var(--accent)', padding: '8px' }}
                          >
                            {headerImg && (
                              <div
                                onClick={uploadHeaderImg}
                                data-cont="img"
                                className={loadingHeaderImg ? styles.imgLoading : undefined}
                              >
                                <img
                                  height={32}
                                  src={headerImg}
                                  style={{ display: 'block', margin: '0 auto' }}
                                  alt=""
                                />
                              </div>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <table
                      width="100%"
                      cellPadding={0}
                      cellSpacing={0}
                      border={0}
                      role="presentation"
                      style={{ background: 'var(--container)' }}
                    >
                      <tbody>
                        <tr>
                          <td style={{ padding: '16px 0 8px 0' }}>
                            <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                              <tbody>
                                <tr>
                                  <td
                                    width={200}
                                    valign="top"
                                    style={{ padding: '0 8px 0 16px', verticalAlign: 'top' }}
                                  >
                                    <ContentEditable
                                      value={_salutation}
                                      setValue={setSalutation}
                                      style={{
                                        color: 'var(--heading)',
                                        fontSize: '56px',
                                        fontFamily: 'var(--heading-font)',
                                        fontWeight: 700,
                                        lineHeight: '50.4px',
                                        wordWrap: 'break-word',
                                        letterSpacing: '-3px',
                                      }}
                                    />
                                    {eventDescription && (
                                      <ContentEditable
                                        value={eventDescription}
                                        setValue={setDescription}
                                        style={{
                                          color: 'var(--heading)',
                                          fontSize: '14px',
                                          fontFamily: 'var(--body-font)',
                                          fontWeight: 500,
                                          lineHeight: '13.3px',
                                          wordWrap: 'break-word',
                                          marginTop: '24px',
                                          width: '104px',
                                          marginLeft: 'auto',
                                          marginRight: 'auto',
                                        }}
                                      />
                                    )}
                                  </td>
                                  <td width={312} valign="top" style={{ padding: '0 8px 0 0', verticalAlign: 'top' }}>
                                    {bodyImg && (
                                      <div
                                        onClick={uploadMain}
                                        data-cont="img"
                                        className={loadingMain ? styles.imgLoading : undefined}
                                      >
                                        <img
                                          width={277}
                                          src={bodyImg}
                                          style={{ display: 'block', marginRight: 'auto' }}
                                          alt=""
                                        />
                                      </div>
                                    )}
                                    <ContentEditable
                                      value={eventTitle}
                                      setValue={setTitle}
                                      style={{
                                        textAlign: 'right',
                                        color: 'var(--heading)',
                                        fontSize: '56px',
                                        fontFamily: 'var(--heading-font)',
                                        fontWeight: 700,
                                        lineHeight: '50.4px',
                                        wordWrap: 'break-word',
                                        letterSpacing: '-3px',
                                      }}
                                    />
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <table
                      width="100%"
                      cellPadding={0}
                      cellSpacing={0}
                      border={0}
                      role="presentation"
                      style={{ background: 'var(--accent)' }}
                    >
                      <tbody>
                        <tr>
                          <td style={{ padding: '24px' }}>
                            <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                              <tbody>
                                <tr>
                                  <td valign="middle" style={{ verticalAlign: 'middle' }}>
                                    {sessionDate && (
                                      <>
                                        <ContentEditable
                                          value={_startIntro}
                                          setValue={setStartIntro}
                                          style={{
                                            color: 'var(--body)',
                                            fontSize: '12px',
                                            fontFamily: 'var(--body-font)',
                                            fontWeight: 500,
                                            lineHeight: '16px',
                                            letterSpacing: '0.24px',
                                            wordWrap: 'break-word',
                                          }}
                                        />
                                        <div
                                          style={{
                                            color: 'var(--body)',
                                            fontSize: '14px',
                                            fontFamily: 'var(--body-font)',
                                            fontWeight: 700,
                                            lineHeight: '20px',
                                            wordWrap: 'break-word',
                                          }}
                                        >
                                          {sessionDate}
                                        </div>
                                      </>
                                    )}
                                  </td>
                                  <td valign="middle" align="right" style={{ verticalAlign: 'middle' }}>
                                    <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                                      <tbody>
                                        <tr>
                                          <td
                                            align="center"
                                            valign="middle"
                                            style={{ background: 'var(--container)', width: '148px', height: '40px' }}
                                          >
                                            <a
                                              href={anchorHref}
                                              onClick={(e) => e.preventDefault()}
                                              style={{
                                                textDecoration: 'none',
                                                padding: '6px 0 6px 0',
                                                width: '148px',
                                                height: '100%',
                                                textAlign: 'center',
                                              }}
                                            >
                                              <ContentEditable
                                                value={_cta}
                                                setValue={setCta}
                                                style={{
                                                  color: 'var(--button-text)',
                                                  fontSize: '13px',
                                                  fontFamily: 'var(--body-font)',
                                                  fontWeight: 600,
                                                  lineHeight: '16px',
                                                  wordWrap: 'break-word',
                                                }}
                                              />
                                            </a>
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td align="center" style={{ paddingTop: '40px' }}>
                    <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        {!!calendarLinks && (
                          <tr>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.iCal}
                              >
                                iCal
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.outlook}
                              >
                                Outlook
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.google}
                              >
                                Google
                              </a>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
